"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useCallback } from "react";
import Link from "next/link";
import { cn } from "@/utilities/core/cn";
import { matchCardBackgroundStyle } from "@/utilities/styles/matchCardBackground";
import { BracketMatchCard, TeamRow } from "./BracketMatchCard";
import type { BracketTie, BracketMatch } from "./types";
const SWIPE_THRESHOLD = 30;
interface BracketTieCardProps {
  tie: BracketTie;
  style?: React.CSSProperties;
}
function MultiLegHeader(t0) {
  const $ = _c(17);
  const {
    match,
    index,
    count,
    onDotClick
  } = t0;
  const isLive = match.status === "live";
  const isScheduled = match.status === "scheduled";
  let t1;
  if ($[0] !== isLive || $[1] !== isScheduled || $[2] !== match.dateLabel || $[3] !== match.statusText) {
    t1 = isLive ? <span className="flex items-center gap-1.5"><span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-primary" /></span><span className="font-bold uppercase text-primary">{match.statusText || "LIVE"}</span></span> : isScheduled && match.statusText ? match.statusText : match.dateLabel;
    $[0] = isLive;
    $[1] = isScheduled;
    $[2] = match.dateLabel;
    $[3] = match.statusText;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="text-[10px] text-muted-foreground">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== count || $[8] !== index || $[9] !== onDotClick) {
    let t4;
    if ($[11] !== index || $[12] !== onDotClick) {
      t4 = (_, i) => <button key={i} onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onDotClick(i);
      }} className={cn("w-1.5 h-1.5 rounded-full transition-all duration-200 cursor-pointer", i === index ? "bg-primary" : "bg-border")} aria-label={`Leg ${i + 1}`} />;
      $[11] = index;
      $[12] = onDotClick;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    t3 = <div className="flex items-center gap-1.5">{Array.from({
        length: count
      }, t4)}</div>;
    $[7] = count;
    $[8] = index;
    $[9] = onDotClick;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[14] !== t2 || $[15] !== t3) {
    t4 = <div className="flex items-center justify-between mb-1.5">{t2}{t3}</div>;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}
function MultiLegCard(t0) {
  const $ = _c(48);
  const {
    tie
  } = t0;
  const {
    matches
  } = tie;
  const [activeIndex, setActiveIndex] = useState(matches.length - 1);
  const touchStartRef = useRef(null);
  const swipingRef = useRef(false);
  const match = matches[activeIndex];
  const isFinished = match.status === "finished";
  const isLive = match.status === "live";
  const hasWinner = isFinished && (match.home.isWinner || match.away.isWinner);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = e => {
      touchStartRef.current = e.touches[0].clientX;
      swipingRef.current = false;
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleTouchStart = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = e_0 => {
      if (touchStartRef.current === null) {
        return;
      }
      const delta = e_0.touches[0].clientX - touchStartRef.current;
      if (Math.abs(delta) > SWIPE_THRESHOLD) {
        swipingRef.current = true;
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleTouchMove = t2;
  let t3;
  if ($[2] !== activeIndex || $[3] !== matches.length) {
    t3 = e_1 => {
      if (touchStartRef.current === null) {
        return;
      }
      const delta_0 = e_1.changedTouches[0].clientX - touchStartRef.current;
      touchStartRef.current = null;
      if (Math.abs(delta_0) > SWIPE_THRESHOLD) {
        swipingRef.current = true;
        if (delta_0 < 0 && activeIndex < matches.length - 1) {
          setActiveIndex(_temp);
        } else {
          if (delta_0 > 0 && activeIndex > 0) {
            setActiveIndex(_temp2);
          }
        }
      }
    };
    $[2] = activeIndex;
    $[3] = matches.length;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleTouchEnd = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e_2 => {
      if (swipingRef.current) {
        e_2.preventDefault();
        swipingRef.current = false;
      }
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleClick = t4;
  const t5 = isLive && "border-primary/50 ring-1 ring-primary/20";
  let t6;
  if ($[6] !== t5) {
    t6 = cn("relative rounded-(--radius) border border-border/50 bg-card/80 backdrop-blur-sm transition-all", "shadow-(--shadow-sm) hover:shadow-(--shadow)", t5);
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== hasWinner) {
    t7 = hasWinner && <div className="absolute left-0 top-2 bottom-2 w-0.75 rounded-full bg-primary" />;
    $[8] = hasWinner;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = hasWinner && "pl-3.5";
  let t9;
  if ($[10] !== t8) {
    t9 = cn("p-2.5", t8);
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== activeIndex || $[13] !== match || $[14] !== matches.length) {
    t10 = <MultiLegHeader match={match} isActive={true} index={activeIndex} count={matches.length} onDotClick={setActiveIndex} />;
    $[12] = activeIndex;
    $[13] = match;
    $[14] = matches.length;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      animation: "fadeInUp 0.2s ease-out"
    };
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== isFinished || $[18] !== match.home) {
    t12 = <TeamRow participant={match.home} isFinished={isFinished} />;
    $[17] = isFinished;
    $[18] = match.home;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="h-px my-1" />;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== isFinished || $[22] !== match.away) {
    t14 = <TeamRow participant={match.away} isFinished={isFinished} />;
    $[21] = isFinished;
    $[22] = match.away;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== match.id || $[25] !== t12 || $[26] !== t14) {
    t15 = <div key={match.id} style={t11}>{t12}{t13}{t14}</div>;
    $[24] = match.id;
    $[25] = t12;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== match.statusNote) {
    t16 = match.statusNote && <div className="mt-1.5 text-[10px] text-muted-foreground text-center">{match.statusNote}</div>;
    $[28] = match.statusNote;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t10 || $[31] !== t15 || $[32] !== t16 || $[33] !== t9) {
    t17 = <div className={t9}>{t10}{t15}{t16}</div>;
    $[30] = t10;
    $[31] = t15;
    $[32] = t16;
    $[33] = t9;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== handleTouchEnd || $[36] !== t17 || $[37] !== t6 || $[38] !== t7) {
    t18 = <div className={t6} style={matchCardBackgroundStyle} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>{t7}{t17}</div>;
    $[35] = handleTouchEnd;
    $[36] = t17;
    $[37] = t6;
    $[38] = t7;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const card = t18;
  const url = match.url;
  let t19;
  if ($[40] !== card || $[41] !== url) {
    t19 = url ? <Link href={url} prefetch={false} className="block" onClick={handleClick}>{card}</Link> : card;
    $[40] = card;
    $[41] = url;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== tie.aggregateLabel) {
    t20 = tie.aggregateLabel && <div className="flex justify-center mt-1.5"><span className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground bg-muted/60 rounded-full px-2.5 py-0.5">{tie.aggregateLabel}</span></div>;
    $[43] = tie.aggregateLabel;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t19 || $[46] !== t20) {
    t21 = <div>{t19}{t20}</div>;
    $[45] = t19;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  return t21;
}
function _temp2(prev_0) {
  return prev_0 - 1;
}
function _temp(prev) {
  return prev + 1;
}
export function BracketTieCard(t0) {
  const $ = _c(13);
  const {
    tie,
    style
  } = t0;
  const isMultiLeg = tie.matches.length > 1;
  if (isMultiLeg) {
    let t1;
    if ($[0] !== tie) {
      t1 = <MultiLegCard tie={tie} />;
      $[0] = tie;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== style || $[3] !== t1) {
      t2 = <div style={style}>{t1}</div>;
      $[2] = style;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t1;
  if ($[5] !== tie.matches) {
    t1 = tie.matches.map(_temp3);
    $[5] = tie.matches;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== tie.aggregateLabel) {
    t2 = tie.aggregateLabel && <div className="flex justify-center mt-1.5"><span className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground bg-muted/60 rounded-full px-2.5 py-0.5">{tie.aggregateLabel}</span></div>;
    $[7] = tie.aggregateLabel;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== style || $[10] !== t1 || $[11] !== t2) {
    t3 = <div style={style}>{t1}{t2}</div>;
    $[9] = style;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
function _temp3(match, i) {
  return <BracketMatchCard key={match.id} match={match} isFirst={i === 0} />;
}